import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { MaskReveal, Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { CTASection } from "../components/CTASection";
import { ReviewSlider } from "../components/ReviewSlider";
import { WhatsAppButton, CallButton } from "../components/WhatsAppButton";
import { Seo } from "../components/Seo";
import { Faq } from "../components/Faq";
import { SERVICES, SERVICES_PATH, GALLERY, HERO_IMAGE, ABOUT_IMAGE, PHONE_DISPLAY } from "../data/siteData";

const Hero = () => {
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 900], [0, 130]);
  const yGlow = useTransform(scrollY, [0, 900], [0, -70]);

  return (
    <section className="relative flex min-h-screen items-center overflow-hidden" data-testid="hero-section">
      <motion.div className="ambient-glow pointer-events-none absolute inset-0" style={{ y: yGlow }} />

      <motion.div
        className="pointer-events-none absolute right-[-6%] top-[8%] hidden h-[84%] w-[38%] lg:block"
        style={{ y: yImg }}
      >
        <motion.div
          className="spotlight-frame h-full w-full overflow-hidden rounded-t-full border border-amber-500/25"
          initial={{ clipPath: "inset(100% 0 0 0)" }}
          animate={{ clipPath: "inset(0% 0 0 0)" }}
          transition={{ duration: 1.4, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <img
            src={HERO_IMAGE}
            alt="Inside Belong Salon & Spa — lounge, corridor, reception and treatment suites"
            className="h-full w-full object-cover"
            fetchpriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C]/70 via-transparent to-[#0B0B0C]/30" />
        </motion.div>
      </motion.div>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-24 pt-36 lg:px-10">
        <MaskReveal delay={0.15}>
          <p className="font-mono-alt text-[11px] uppercase tracking-[0.35em] text-amber-400/80" data-testid="hero-eyebrow">
            Belong Salon & Spa — Suchitra, Hyderabad
          </p>
        </MaskReveal>

        <h1 className="mt-8 font-display text-5xl font-light leading-[1.02] tracking-tight text-amber-50 sm:text-7xl lg:text-[6.5rem]">
          <MaskReveal delay={0.3}>Where the body</MaskReveal>
          <MaskReveal delay={0.45}>learns to</MaskReveal>
          <MaskReveal delay={0.6}>
            <em className="text-gold-gradient not-italic">belong.</em>
          </MaskReveal>
        </h1>

        <MaskReveal delay={0.85} className="mt-10 max-w-md">
          <p className="text-base font-light leading-relaxed text-stone-400 sm:text-lg">
            A massage-only sanctuary. Seven signature services, private candlelit suites, and hands
            that remember what the week made you forget.
          </p>
        </MaskReveal>

        <motion.div
          className="mt-12 flex flex-col gap-4 sm:flex-row"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <CallButton label={PHONE_DISPLAY} testid="hero-call-btn" />
          <WhatsAppButton testid="hero-whatsapp-booking-btn" />
        </motion.div>

        <motion.div
          className="mt-20 grid max-w-lg grid-cols-3 gap-6 border-t border-white/10 pt-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3, duration: 1 }}
          data-testid="hero-stats"
        >
          {[
            ["07", "Signature services"],
            ["45–120", "Minute sessions"],
            ["100%", "Private suites"],
          ].map(([num, label]) => (
            <div key={label}>
              <p className="font-display text-3xl font-light text-amber-200 sm:text-4xl">{num}</p>
              <p className="mt-1 font-mono-alt text-[9px] uppercase tracking-[0.22em] text-stone-500">{label}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

const Philosophy = () => (
  <section className="relative py-28 lg:py-36" data-testid="philosophy-section">
    <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 lg:grid-cols-2 lg:px-10">
      <Reveal>
        <p className="font-mono-alt text-[11px] uppercase tracking-[0.3em] text-amber-400/80">The Sanctuary</p>
        <h2 className="mt-6 font-display text-3xl font-light leading-[1.12] tracking-tight text-amber-50 sm:text-4xl lg:text-5xl">
          Devoted to a single art — <em className="text-gold-gradient not-italic">the healing of the body.</em>
        </h2>
        <p className="mt-8 max-w-lg text-base font-light leading-relaxed text-stone-400">
          No salons. No noise. No hurry. Belong exists for one purpose: massage and spa services
          performed slowly, in private, by therapists who treat touch as a discipline.
        </p>
        <p className="mt-5 max-w-lg text-base font-light leading-relaxed text-stone-400">
          Every suite is candlelit and yours alone. Every oil is blended in-house. Every session
          ends only when the body says so.
        </p>
        <Link
          to="/about-us-luxury-spa"
          data-testid="philosophy-about-link"
          className="group mt-10 inline-flex items-center gap-3 font-mono-alt text-[11px] uppercase tracking-[0.25em] text-amber-300 transition-colors hover:text-amber-100"
        >
          Our Story
          <span className="flex h-9 w-9 items-center justify-center rounded-full border border-amber-500/30 transition-all duration-300 group-hover:border-amber-300 group-hover:bg-amber-400 group-hover:text-[#0B0B0C]">
            <ArrowUpRight className="h-4 w-4" />
          </span>
        </Link>
      </Reveal>

      <Reveal delay={0.15} className="relative">
        <div className="spotlight-frame overflow-hidden rounded-3xl border border-amber-500/20">
          <img
            src={ABOUT_IMAGE}
            alt="Belong Salon & Spa reception lounge in Suchitra"
            className="aspect-[16/10] w-full object-cover"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C]/50 to-transparent" />
        </div>
      </Reveal>
    </div>
  </section>
);

const ServiceIndex = () => (
  <section className="py-28 lg:py-32" data-testid="service-index-section">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="font-mono-alt text-[11px] uppercase tracking-[0.3em] text-amber-400/80">The Menu</p>
          <h2 className="mt-5 font-display text-3xl font-light tracking-tight text-amber-50 sm:text-4xl lg:text-5xl">
            Seven services. <em className="text-gold-gradient not-italic">One intention.</em>
          </h2>
        </div>
        <Link
          to={SERVICES_PATH}
          data-testid="service-index-view-all"
          className="group inline-flex items-center gap-2 font-mono-alt text-[11px] uppercase tracking-[0.25em] text-stone-400 transition-colors hover:text-amber-200"
        >
          View all services
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>

      <div className="mt-16">
        {SERVICES.map((s, i) => (
          <Reveal key={s.slug} delay={i * 0.04}>
            <Link
              to={`${SERVICES_PATH}/${s.slug}`}
              data-testid={`home-service-row-${s.slug}`}
              className="group flex items-center gap-5 border-b border-white/5 py-7 transition-colors duration-500 hover:border-amber-500/30 sm:gap-8"
            >
              <span className="font-mono-alt text-xs text-amber-500/60">{String(i + 1).padStart(2, "0")}</span>
              <div className="min-w-0 flex-1">
                <h3 className="truncate font-display text-2xl font-light text-stone-200 transition-colors duration-300 group-hover:text-amber-200 sm:text-4xl">
                  {s.name}
                </h3>
                <p className="mt-1 hidden text-sm font-light text-stone-500 sm:block">{s.tagline}</p>
              </div>
              <span className="hidden shrink-0 font-mono-alt text-[10px] uppercase tracking-[0.2em] text-stone-500 md:block">
                {s.durations[0]} — {s.durations[s.durations.length - 1]}
              </span>
              <span className="relative hidden h-16 w-24 shrink-0 overflow-hidden rounded-lg border border-amber-500/10 lg:block">
                <img
                  src={s.image}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover opacity-60 transition-all duration-700 group-hover:scale-110 group-hover:opacity-100"
                />
              </span>
              <ArrowUpRight className="h-5 w-5 shrink-0 text-stone-600 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-amber-300" />
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Ambience = () => (
  <section className="py-10" data-testid="ambience-section">
    <div className="mx-auto grid max-w-7xl gap-5 px-6 sm:grid-cols-3 lg:px-10">
      {GALLERY.map((src, i) => (
        <Reveal key={src} delay={i * 0.12}>
          <div
            className={`spotlight-frame overflow-hidden border border-amber-500/15 ${
              i === 1 ? "rounded-t-full" : "rounded-2xl"
            } ${i === 1 ? "sm:-translate-y-8" : ""}`}
          >
            <img
              src={src}
              alt="Belong spa ambience"
              className="aspect-[3/4] w-full object-cover"
              loading="lazy"
              decoding="async"
            />
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const BookingCTA = () => (
  <section
    id="book"
    className="relative overflow-hidden py-28"
    data-testid="home-booking-cta"
    style={{
      backgroundImage: "url(/images/belong-treatment-room.png)",
      backgroundSize: "cover",
      backgroundPosition: "center",
    }}
  >
    <div className="absolute inset-0 bg-[#0B0B0C]/70" />
    <div className="absolute inset-0 bg-gradient-to-b from-[#0B0B0C] via-transparent to-[#0B0B0C]" />
    <div className="relative mx-auto max-w-4xl rounded-[2rem] border border-amber-500/20 bg-[#0B0B0C]/70 px-6 py-14 text-center backdrop-blur-sm sm:px-14">
      <Reveal>
        <p className="font-mono-alt text-[11px] uppercase tracking-[0.3em] text-amber-400/90">
          Reserve Your Suite
        </p>
        <h2 className="mt-6 font-display text-4xl font-light leading-[1.08] tracking-tight text-amber-50 sm:text-5xl">
          Three ways to <em className="text-gold-gradient not-italic">book.</em>
        </h2>
        <p className="mx-auto mt-6 max-w-xl text-base font-light leading-relaxed text-stone-300">
          Call us, message us, or pick your service and time slot online — whichever feels easiest,
          your suite is waiting.
        </p>
        <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
          <CallButton label={PHONE_DISPLAY} testid="home-cta-call-btn" />
          <WhatsAppButton testid="home-cta-whatsapp-btn" />
          <Link
            to="/contact-us-luxury-spa#booking"
            data-testid="home-book-appointment-btn"
            className="inline-flex items-center justify-center gap-3 rounded-full bg-amber-400 px-9 py-4 font-mono-alt text-xs uppercase tracking-[0.22em] text-[#0B0B0C] transition-all duration-300 hover:bg-amber-300 hover:shadow-[0_0_35px_rgba(212,175,55,0.35)]"
          >
            Book Appointment
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

const Voices = () => (
  <section className="py-28" data-testid="reviews-section">
    <div className="mx-auto max-w-5xl px-6 lg:px-10">
      <Reveal>
        <p className="font-mono-alt text-[11px] uppercase tracking-[0.3em] text-amber-400/80">Whispers from the suites</p>
        <h2 className="mt-5 font-display text-3xl font-light tracking-tight text-amber-50 sm:text-4xl">
          Words from our guests
        </h2>
      </Reveal>
      <Reveal delay={0.15} className="mt-14">
        <ReviewSlider />
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Seo
        title="Body Massage & Luxury Spa in Suchitra, Hyderabad | Belong Salon & Spa"
        description="Belong Salon & Spa — massage-only luxury spa in Suchitra, Hyderabad. Aroma, Balinese, deep tissue, Thai & four hand massage, body massage, private Jacuzzi spa. Book on WhatsApp."
        keywords="spa in Suchitra, body massage Hyderabad, massage spa Quthbullapur, luxury spa Hyderabad, Jacuzzi spa Hyderabad, Belong Salon and Spa"
      />
      <Hero />
      <Marquee />
      <Philosophy />
      <ServiceIndex />
      <Ambience />
      <BookingCTA />
      <Voices />
      <Faq />
      <CTASection />
    </>
  );
}
